export const COMPLIANCE_DASH = ["Designed to Align with HIPAA", "Designed to Align with FERPA", "Nursys API", "256-Bit Vault Encryption"];
export const COMPLIANCE_DASH_LINKS = {
  "Designed to Align with HIPAA": "https://www.hhs.gov/hipaa/index.html",
  "Designed to Align with FERPA": "https://studentprivacy.ed.gov/ferpa",
  "Nursys API": "https://www.nursys.com/",
};
export const COMPLIANCE_DASH_NOTE =
  "These reflect the standards our data-handling practices are designed around, not certifications held directly by Acuity Rise. Acuity Rise's digital transmittals and data pass-through gateways are hosted on Zoho Mail's SOC 2 Type II and ISO/IEC 27001 certified infrastructure, utilizing AES-256 encryption at rest and TLS 1.2/1.3 in transit. A formal, independent security attestation for Acuity Rise itself is not yet in place.";

export const LEADERSHIP_NAMED = [
  {
    name: "Nadine Lindsay, DNP, CRNP, APRN, FNP-C",
    title: "Chief Clinical Officer",
    bio: "19+ years in nursing; practicing Family Nurse Practitioner and Managing Partner at DelCare Health Solutions; Doctor of Nursing Practice, Widener University; preceptor and clinical educator since 2012; Board Member, Delaware Board of Nursing (2025); Pennsylvania Action Coalition Clinical Faculty & Preceptor Advisory Board.",
  },
];

// Nameless — positions in active candidate review. No name is published until
// conflict-of-interest screening and onboarding are complete for each seat.
export const LEADERSHIP_OPEN_ROLES = [
  {
    title: "Chief Compliance Officer",
    function:
      "Owns the integrity of every credentialing, screening, and compliance claim Acuity Rise makes — license verification, background/drug-screening oversight, adverse-action procedure, and audit-ready documentation for university and health-system partners.",
    credentials:
      "Background in healthcare compliance, clinical credentialing, or healthcare risk management; working knowledge of FCRA adverse-action requirements and FERPA/HIPAA-adjacent data handling; Certified in Healthcare Compliance (CHC) preferred.",
  },
  {
    title: "Director of Clinical Operations",
    function:
      "Runs day-to-day placement execution once a partnership is signed — matching preceptors to programs, managing scheduling across universities and clinical sites, and interoperating with each partner's clinical-tracking platform (Typhon, CORE ELMS, Exxat, myClinicalExchange, E*Value).",
    credentials:
      "Background in nursing education, clinical placement coordination, or healthcare operations; experience working with university placement offices or hospital/FQHC credentialing departments preferred.",
  },
];

export const TRACKS = [
  {
    id: "universities",
    tab: "Universities",
    overline: "Who We Are & Who We Serve",
    headline: "The Enterprise Clinical Placement Engine for Advanced Practice Nursing Programs",
    description:
      "Eliminate graduate clinical placement bottlenecks, protect CCNE/ACEN accreditation standings, and scale hybrid and distance cohorts through university-credentialed faculty preceptors who handle direct supervision and chart sign-offs — across MSN, MSN-Ed, FNP, PMHNP, AGNP, PNP, and WHNP programs.",
    image: "https://images.pexels.com/photos/16173473/pexels-photo-16173473.jpeg?auto=compress&w=1200",
    offerings: [
      { icon: "Network", title: "Faculty Preceptor Deployment", desc: "University-credentialed faculty preceptors providing direct supervision and chart sign-offs across every specialty cohort." },
      { icon: "ShieldCheck", title: "CCNE / ACEN Audit Protection", desc: "Audit-ready preceptor compliance packets and evidence architecture mapped to accreditation site-visit standards." },
      { icon: "MonitorCog", title: "Typhon / Exxat / Meditrek Sync", desc: "Verified hours, evaluations, and Nursys primary-source license verification flowing into your system of record." },
      { icon: "Home", title: "PillarPoint Housing & Earn-While-You-Learn", desc: "Corporate housing and relocation frameworks for out-of-state, online, and hybrid students, paired with the Acuity Earn-While-You-Learn Engine." },
    ],
    systems: ["Nursys Instant Verification", "Typhon / Exxat / Meditrek Live Sync", "CCNE / ACEN Evidence Mapping", "Sample Compliance Packet Center"],
    specialties: [
      { icon: "Brain", name: "PMHNP Track" },
      { icon: "HeartPulse", name: "AGNP Track" },
      { icon: "Baby", name: "PNP Track" },
      { icon: "Stethoscope", name: "FNP Track" },
      { icon: "BookOpen", name: "MSN-Ed Track" },
      { icon: "Users", name: "WHNP Track" },
    ],
    roadmap: ["Program Audit & Accreditation Mapping", "Affiliation & Faculty Architecture", "Preceptor Network Activation", "Cohort Placement Cycle", "CCNE/ACEN Reporting Cadence"],
    accordion: [
      { q: "How are faculty preceptors credentialed?", a: "Every preceptor passes Nursys primary-source license verification, national certification review, specialty alignment, and structured orientation with student evaluation duties before cohort assignment." },
      { q: "Can you support fully online and out-of-state cohorts?", a: "Yes — hybrid and distance cohorts are our core competency, supported by PillarPoint corporate housing and relocation frameworks for rotating students." },
      { q: "What compliance documentation do we receive?", a: "Sample completed Preceptor Compliance Packets and CCNE/CNE audit-ready packets are previewable during intake; live engagements receive full evidence sets on your reporting cadence." },
    ],
    support: ["PillarPoint Corporate Housing & Relocation Framework", "Acuity Earn-While-You-Learn Engine"],
    cta: "Secure Your Program's Clinical Footprint",
  },
  {
    id: "facilities",
    tab: "Health Systems",
    overline: "Who We Are & Who We Serve",
    headline: "Transform Your FQHC & Facility Operations: A Zero-Cost, Plug-and-Play Clinical Partnership",
    description:
      "Expand patient capacity and eradicate provider chart backlogs at zero cost to your FQHC. Host sites incur zero supervision tax — external university faculty preceptors act as billing providers of record, allowing your organization to retain 100% of PPS billing revenue.",
    image: "https://images.pexels.com/photos/33812025/pexels-photo-33812025.jpeg?auto=compress&w=1200",
    offerings: [
      { icon: "TrendingUp", title: "Staffing & Capacity — Zero Cost", desc: "Faculty preceptors serve as billing providers of record; your FQHC retains 100% of PPS revenue with zero supervision tax." },
      { icon: "Workflow", title: "3-Phase Deployment Roadmap", desc: "Phase 1: Scribing & Onboarding → Phase 2: Collaborative Care → Phase 3: Autonomous Pod Operations." },
      { icon: "FileCheck2", title: "60-Day Credentialing Runway", desc: "Pre-vetted credentialing packets delivered 60 days prior to deployment with dual-sided EHR pre-training." },
      { icon: "Briefcase", title: "Job Vacancy Posting Engine", desc: "Post and fill open vacancies for preceptors, international medical students, and graduate nurses from our talent pool." },
    ],
    systems: ["HRSA / UDS Metric Alignment", "Epic · Cerner · eCW · Athena Pre-Training", "PPS Revenue Retention Architecture", "60-Day Credentialing Runway"],
    specialties: [
      { icon: "Hospital", name: "Acute Care" },
      { icon: "Home", name: "Ambulatory" },
      { icon: "Brain", name: "Behavioral Health" },
      { icon: "Globe", name: "FQHC / Community" },
      { icon: "Syringe", name: "Specialty Clinics" },
    ],
    roadmap: ["Operational Assessment", "Phase 1 — Scribing & Onboarding", "Phase 2 — Collaborative Care", "Phase 3 — Autonomous Pod Operations", "UDS / HRSA Metric Review"],
    accordion: [
      { q: "What does 'zero cost' actually mean?", a: "Because external university faculty preceptors act as billing providers of record, host sites carry no supervision burden or cost — your organization retains full PPS encounter revenue while expanding capacity." },
      { q: "How does the 60-day credentialing runway work?", a: "Pre-vetted packets arrive 60 days before deployment: licensure, certifications, immunizations, and background verifications, sequenced against your medical staff office timeline." },
      { q: "Can we recruit from the rotation pipeline?", a: "Yes — the Job Vacancy Posting Engine lets your system post open roles directly to our preceptor, IMG, and graduate nurse talent pool." },
      { q: "What kind of revenue can we expect? (Sample Deployment Model — Projected, Not a Live Client Result)", a: "Modeled on current CMS PPS reimbursement schedules for a mid-size FQHC hosting one 4-student NP pod for a full year: base encounter rate $207.72 (2026 CMS PPS), new-patient/IPPE/AWV encounter rate approximately $278.68, and an estimated annual encounter volume of roughly 1,700–1,900 per pod — projecting annual host revenue of approximately $375,000–$530,000, 100% retained by the host facility since faculty preceptors bill as providers of record. Acuity Rise's administrative fee is 10–15% of encounter revenue, well below typical MSO administrative retention, and covers credentialing, compliance, and preceptor coordination. This is a projected model based on current CMS rate schedules and typical pod utilization, not a guarantee or a completed client engagement — individual results depend on patient volume, payer mix, and site-specific factors." },
    ],
    support: ["PillarPoint Corporate Housing (traveling specialists & incoming clinicians)", "Acuity Workforce Engine"],
    roi: true,
    cta: "Expand Your Facility's Capacity",
  },
  {
    id: "pediatric",
    tab: "Pediatric Facilities",
    overline: "Who We Are & Who We Serve",
    headline: "Turnkey Pediatric Clinical Pods & Workforce Expansion",
    description:
      "Deploy dedicated PNP and FNP student pods into high-acuity pediatric environments — ensuring rigorous exposure to well-child checks, developmental screenings, and acute pediatric pathologies under hardcoded pediatric compliance standards.",
    image: "https://images.pexels.com/photos/3985149/pexels-photo-3985149.jpeg?auto=compress&w=1200",
    offerings: [
      { icon: "Baby", title: "Dedicated Pediatric Pods", desc: "PNP and FNP student pods engineered for well-child, developmental screening, and acute pediatric pathology exposure." },
      { icon: "ShieldCheck", title: "Hardcoded Pediatric Compliance", desc: "Child and family confidentiality standards, pediatric clinical hour thresholds, and NJ APN practice rules enforced by design." },
      { icon: "Activity", title: "Live ICD-10 Pediatric Tracking", desc: "Real-time pediatric diagnostic tracking across every pod encounter for competency documentation." },
      { icon: "Briefcase", title: "Pediatric Vacancy Engine", desc: "Post and fill pediatric specialist vacancies directly from our credentialed talent pool." },
    ],
    systems: ["Dual-Sided Pediatric EHR Pre-Training", "ICD-10 Pediatric Diagnostic Tracking", "Pediatric Compliance Frameworks"],
    specialties: [
      { icon: "Baby", name: "Well-Child & Developmental" },
      { icon: "Activity", name: "Acute Pediatric Pathologies" },
      { icon: "Hospital", name: "PICU / High-Acuity" },
      { icon: "Syringe", name: "Pediatric Sub-Specialty" },
    ],
    roadmap: ["Facility Acuity Assessment", "Pediatric Compliance Mapping", "Pod Deployment & EHR Pre-Training", "Live Diagnostic Tracking", "Workforce Conversion Review"],
    accordion: [
      { q: "Which student populations rotate in pediatric pods?", a: "CPNP-PC/CPNP-AC-track PNP students and FNP students requiring lifespan pediatric exposure, all under pediatric-credentialed preceptor oversight." },
      { q: "How is patient confidentiality protected?", a: "Child and family confidentiality compliance is hardcoded into pod protocols, charting workflows, and preceptor countersignature requirements." },
    ],
    support: ["PillarPoint Corporate Housing (visiting pediatric specialists)", "Acuity Workforce Engine"],
    cta: "Deploy Pediatric Clinical Pods",
  },
  {
    id: "homecare",
    tab: "Homecare Networks",
    overline: "Who We Are & Who We Serve",
    headline: "Advanced Home Health Workforce Infrastructure & Clinical QA",
    description:
      "Deploy MSN-Ed clinical supervisors, LPN trainees, and advanced practice coordinators for home care assessment, chart QA, utilization review, and care coordination — under strict preceptor oversight and scope insulation.",
    image: "https://images.pexels.com/photos/32213424/pexels-photo-32213424.jpeg?auto=compress&w=1200",
    offerings: [
      { icon: "ClipboardList", title: "Chart QA & Utilization Review", desc: "Structured chart quality assurance and UR workflows executed by supervised advanced practice coordinators." },
      { icon: "ShieldCheck", title: "Strict Scope Insulation", desc: "CHHA/LPN duties cleanly separated from APRN advanced procedures with preceptor-governed protocols." },
      { icon: "MonitorCog", title: "Home Health EHR Pre-Training", desc: "Charting pre-training for home health platforms so clinical teams arrive deployment-ready." },
      { icon: "Briefcase", title: "Direct Recruitment Board", desc: "Post home health vacancies directly to our supervised clinical talent pipeline." },
    ],
    systems: ["Home Health EHR Charting Pre-Training", "Scope-of-Practice Matrix Governance", "Care Coordination Frameworks"],
    specialties: [
      { icon: "Home", name: "Home Care Assessment" },
      { icon: "FileCheck2", name: "Chart QA" },
      { icon: "Activity", name: "Utilization Review" },
      { icon: "Users", name: "Care Coordination" },
    ],
    roadmap: ["Agency Licensure Review", "Scope-of-Practice Mapping", "Supervisor & Trainee Deployment", "QA / UR Cadence", "Recruitment Pipeline Activation"],
    accordion: [
      { q: "How is scope of practice controlled?", a: "A hardcoded scope-of-practice matrix insulates CHHA/LPN duties from APRN advanced procedures, with every escalation governed by preceptor oversight." },
      { q: "Which staffing configurations are available?", a: "MSN-Ed clinical supervisors, LPN trainees, and APRN care coordinators — deployed individually or as integrated home health pods." },
    ],
    support: ["Acuity Workforce Engine"],
    cta: "Integrate Homecare Clinical Pods",
  },
  {
    id: "lpn",
    tab: "Vocational Nursing Schools",
    overline: "Who We Are & Who We Serve",
    headline: "Turnkey Sub-Acute Clinical Infrastructure & NCLEX-PN Safeguard System",
    description:
      "Solve sub-acute and SNF clinical site displacement, eliminate faculty bottlenecks, and protect NCLEX-PN first-time pass rates — with BSN/MSN-Ed shared clinical faculty and real-time competency tracking mapped to State Board standards.",
    image: "https://images.pexels.com/photos/32213424/pexels-photo-32213424.jpeg?auto=compress&w=1200",
    offerings: [
      { icon: "MapPin", title: "Track 1 — Foundations & LTC", desc: "Foundational and long-term-care rotations with executed affiliation frameworks and instructor coverage." },
      { icon: "Activity", title: "Track 2 — Sub-Acute Med-Surg 1 & 2 / Pharmacology", desc: "Progressive sub-acute med-surg and pharmacology rotations mapped to State Board skills standards." },
      { icon: "UserCheck", title: "Shared Clinical Faculty", desc: "BSN/MSN-Ed clinical faculty deployment protecting student-to-faculty ratios per NJ DOH-licensed facility standards." },
      { icon: "GraduationCap", title: "NCLEX-PN Safeguard System", desc: "Real-time NCLEX-PN competency and remediation tracking aligned to LPN Board of Nursing scope and bedside competency frameworks." },
    ],
    systems: ["State Board Skills Mapping", "Dual-Sided Sub-Acute EHR Pre-Training", "NCLEX-PN Competency & Remediation Tracking"],
    specialties: [
      { icon: "Activity", name: "Sub-Acute Care" },
      { icon: "Accessibility", name: "Long-Term Care" },
      { icon: "Pill", name: "Pharmacology Rotations" },
      { icon: "Users", name: "Geriatrics" },
    ],
    roadmap: ["Program Intake & Board Status Review", "Site Network Matching", "Shared Faculty Alignment", "Track 1 & 2 Rotation Execution", "NCLEX-PN Readiness Reporting"],
    accordion: [
      { q: "How are first-time pass rates protected?", a: "Real-time NCLEX-PN competency tracking flags remediation needs during rotations — not after — mapped directly to State Board bedside competency frameworks." },
      { q: "What is the Promise Endowment Scholarship?", a: "A need- and merit-based annual award funded by reallocating a dedicated portion of mandatory Per-Student Clinical Placement Fees — no external fundraising required." },
      { q: "What does the program fee fund?", a: "Unlike our health-system and university placements — where Acuity Rise absorbs 100% of the cost in exchange for retained billing revenue — the vocational/LPN track runs on a transparent, fixed per-student program fee. That fee funds transportation stipends for preceptors and MSN-Ed students traveling to clinical sites, Acuity Rise's administrative and compliance infrastructure for the placement, and the Promise Endowment Scholarship fund every subscribing school's students become eligible for." },
    ],
    support: [],
    spotlight: {
      title: "The Promise Endowment Scholarship Mechanics",
      desc: "A need- and merit-based annual award funded by reallocating a dedicated portion of mandatory Per-Student Clinical Placement Fees — embedding student support directly into placement economics.",
    },
    cta: "Partner With Our Vocational Nursing Clinical Network",
  },
  {
    id: "preceptors",
    tab: "Preceptors",
    overline: "Target Clinical Practitioner Profiles & Faculty Pathways",
    headline: "Elevate Your Clinical Practice: The Premier Network for Advanced Practice Preceptors",
    description:
      "Precept without burnout. A governed end-to-end pipeline — Intake → Assessment → Background Checks → Onboarding — with enterprise preceptorship training, national board certification sponsorship (CNE, CNEcl), and access to Autonomous Practice Suites under N.J.A.C. 13:35-6.17.",
    image: "https://images.pexels.com/photos/3985149/pexels-photo-3985149.jpeg?auto=compress&w=1200",
    offerings: [
      { icon: "Award", title: "Senior APRN Preceptors", desc: "Active licensure, national certification, 1+ year experience — premium engagements with structured evaluation instruments." },
      { icon: "BookOpen", title: "Emerging Clinical Educators (MSN-Ed)", desc: "Curriculum design, simulation, learner assessment, and LPN clinical instruction pathways." },
      { icon: "Compass", title: "Non-Credentialed Experienced Educators", desc: "Path A: Free Enterprise Credentialing. Path B: Resource Library Architecture — two governed routes into faculty standing." },
      { icon: "Medal", title: "Executive Nurse Emeriti", desc: "Advisory precepting, program governance, and legacy mentorship appointments." },
    ],
    systems: ["Intake → Assessment → Background Checks → Onboarding", "CNE / CNEcl Certification Sponsorship", "N.J.A.C. 13:35-6.17 Autonomous Practice Access", "256-Bit Encrypted Credential Vault"],
    specialties: [
      { icon: "Stethoscope", name: "FNP" },
      { icon: "Brain", name: "PMHNP" },
      { icon: "HeartPulse", name: "AGNP" },
      { icon: "Baby", name: "PNP" },
      { icon: "BookOpen", name: "MSN-Ed" },
    ],
    roadmap: ["Credential Submission to Encrypted Vault", "Assessment & Background Verification", "Enterprise Preceptorship Onboarding", "Student Match & Orientation", "Honoraria & Recognition Cycle"],
    accordion: [
      { q: "What are the eligibility standards?", a: "CCNE/CNE-aligned: active licensure, national certification, 1+ year specialty experience, student orientation protocols, and defined evaluation duties." },
      { q: "What does certification sponsorship include?", a: "National board certification sponsorship for CNE and CNEcl credentials, plus enterprise preceptorship training in your specialty niche." },
      { q: "How do I reach the compliance team directly?", a: "Use the General Inquiries option in this section — your message routes to our compliance operations desk with an audit-logged receipt." },
    ],
    support: ["PillarPoint Corporate Housing (visiting & relocating preceptors)", "Acuity Workforce Engine"],
    cta: "Join the Preceptor Faculty Network",
  },
  {
    id: "img",
    tab: "Global USCE",
    overline: "Who We Are & Who We Serve",
    headline: "US Clinical Experience (USCE) — Structured, Compliant, ERAS-Ready",
    description:
      "Acuity Rise builds structured US clinical exposure pathways for international medical students and graduates preparing for the residency Match — designed around current US visa regulations, not around promises we can't legally make. Acuity Rise does not sponsor visas, does not guarantee visa approval, and does not provide compensation for USCE placements; participants are responsible for securing their own appropriate visa status and are strongly encouraged to consult independent immigration counsel before applying.",
    image: "https://images.pexels.com/photos/36380795/pexels-photo-36380795.jpeg?auto=compress&w=1200",
    offerings: [
      { icon: "Globe", title: "Structured Observerships", desc: "Observership placements for graduated IMGs (typically B-1/B-2 visitor status) with real US physician supervision and letter-of-recommendation potential — unpaid, no hands-on patient contact, per current federal visa regulations." },
      { icon: "FileSignature", title: "ERAS LoR Vault", desc: "Primary-source verified letter-of-recommendation pipeline with secure file transmission to ERAS timelines." },
      { icon: "MonitorCog", title: "EHR & ICD-10 Readiness", desc: "Dual-sided EHR pre-training and real-time ICD-10 diagnostic tracking across every clerkship encounter." },
      { icon: "Users", title: "IPE Care Units", desc: "Interprofessional Education units embedding IMGs alongside NP and nursing cohorts for team-based U.S. practice fluency." },
    ],
    systems: ["ERAS Timeline Mapping", "Primary-Source LoR Verification", "USMLE Schedule Protection", "ICD-10 Diagnostic Tracking"],
    specialties: [
      { icon: "Stethoscope", name: "Internal Medicine" },
      { icon: "Users", name: "Family Medicine" },
      { icon: "Baby", name: "Pediatrics" },
      { icon: "Brain", name: "Psychiatry" },
    ],
    roadmap: ["ECFMG Status Intake", "Document Verification", "Clerkship Sequencing", "Clinical Rotations & LoR Pipeline", "ERAS & Match Support"],
    accordion: [
      { q: "How are match rates protected?", a: "Clerkship sequencing is engineered backward from ERAS deadlines and USMLE timelines, with primary-source verified LoRs from attendings experienced in residency selection." },
      { q: "What support exists on arrival?", a: "PillarPoint provides soft-landing corporate housing, relocation assistance, and concierge support. Note: nearly all US clinical experience for this population is unpaid under current federal regulations — Acuity Rise does not provide compensation for USCE placements." },
    ],
    support: ["PillarPoint Corporate Housing & Relocation (soft-landing + concierge)"],
    cta: "Accelerate Your U.S. Residency Match Rates",
  },
];

export const TRACK_BULLETS = {
  universities: ["Nursys primary-source verification on every preceptor", "CCNE/ACEN audit-ready evidence packets", "Typhon / Exxat / Meditrek live sync"],
  facilities: ["Zero supervision tax — faculty preceptors bill as providers of record", "100% PPS revenue retention", "60-day credentialing runway"],
  pediatric: ["Hardcoded pediatric compliance standards", "Live ICD-10 pediatric diagnostic tracking", "Dual-sided pediatric EHR pre-training"],
  homecare: ["Strict CHHA/LPN vs APRN scope insulation", "Preceptor-governed QA and UR workflows", "Home health EHR charting pre-training"],
  lpn: ["State Board-mapped skills documentation", "BSN/MSN-Ed shared clinical faculty", "Real-time NCLEX-PN remediation tracking"],
  preceptors: ["Governed pipeline: Intake → Assessment → Background Checks → Onboarding", "CNE / CNEcl certification sponsorship", "256-bit encrypted credential vault"],
  img: ["ACGME-aligned FQHC and hospital settings", "Primary-source verified ERAS LoR vault", "USMLE timeline protection"],
};

export const SPECIALTY_DETAILS = {
  universities: {
    "PMHNP Track": "Requires active PMHNP/psychiatrist credentials, national certification (PMHNP-BC), 1+ year experience, ≥500 faculty-supervised clinical hours across two psychotherapy modalities, and adherence to NJ 24-month/2,400-hour collaborative joint protocol rules with DEA/CDS registration.",
    "AGNP Track": "Primary & Acute Care — requires population-specific certification (AGPCNP-BC or AGACNP-BC), 1+ year experience, restricted scope alignment, graduate pharmacology requirements, and NJ practice threshold standards.",
    "PNP Track": "Primary & Acute Care — requires CPNP-PC or CPNP-AC certification, pediatric clinical hour thresholds, child/family confidentiality compliance, and NJ APN private-practice rules.",
    "FNP Track": "Requires lifespan primary care supervision, FNP-C/FNP-BC certification, 1+ year experience, 3-credit graduate pharmacology (or 45 integrated hours), and NJ prescriptive joint protocols.",
    "MSN-Ed Track": "Clinical Educator & LPN Supervisory Track — curriculum design, simulation, and learner assessment. Incorporates NJ DOH-licensed facility RN preceptor standards (BSN/MSN/doctorate or 5 years experience) with business structuring scope clarified within active RN licensure.",
    "WHNP Track": "Requires WHNP-BC or CNM credentials, women's health clinical hour supervision, gynecologic assessment oversight, and NJ APN compliance.",
  },
  facilities: {
    "Acute Care": "Med-surg, telemetry, and critical care pod capacity converting precepting bandwidth into governed workforce pipeline.",
    "Ambulatory": "Clinic-based pods with dual-sided EHR pre-training so students contribute to throughput from day one.",
    "Behavioral Health": "Behavioral health pod architecture with safety-first orientation and PMHNP pipeline development.",
    "FQHC / Community": "Zero-cost partnership: faculty preceptors as billing providers of record, UDS/HRSA metric alignment, full PPS retention.",
    "Specialty Clinics": "Specialty pods — cardiology, endocrinology, women's health — with credentialing handled end-to-end.",
  },
  pediatric: {
    "Well-Child & Developmental": "Structured well-child check and developmental screening exposure with pediatric-credentialed oversight.",
    "Acute Pediatric Pathologies": "Rigorous acute pathology rotations with live ICD-10 pediatric diagnostic tracking.",
    "PICU / High-Acuity": "High-acuity pod deployment with hardcoded pediatric compliance and confidentiality standards.",
    "Pediatric Sub-Specialty": "Sub-specialty clinic pods matched to facility acuity demographics.",
  },
  homecare: {
    "Home Care Assessment": "Supervised in-home assessment workflows with scope-insulated protocols.",
    "Chart QA": "Chart quality assurance executed by MSN-Ed supervisors and coordinators under preceptor governance.",
    "Utilization Review": "UR workflows aligned to payer and licensure requirements with audit-ready documentation.",
    "Care Coordination": "Advanced practice care coordination across the home health episode of care.",
  },
  lpn: {
    "Sub-Acute Care": "Dedicated sub-acute unit placements with executed affiliation agreements and instructor coverage.",
    "Long-Term Care": "LTC rotations structured around facility census with board-audit-ready documentation.",
    "Pharmacology Rotations": "Track 2 pharmacology rotations mapped to State Board skills checklists.",
    "Geriatrics": "Geriatric-focused competency instruments tailored to practical nursing scope.",
  },
  preceptors: {
    "FNP": "Board-specified qualifying practice hours: 500–675 direct patient care hours across lifespan primary care.",
    "PMHNP": "Board-specified qualifying practice hours: 500–600 direct psychiatric/behavioral health hours (≥500 faculty-supervised across two psychotherapy modalities).",
    "AGNP": "Board-specified qualifying practice hours: 500–600 direct clinical hours across adult-gerontology settings.",
    "PNP": "Board-specified qualifying practice hours: 500+ direct pediatric clinical hours.",
    "MSN-Ed": "Board-specified qualifying practice hours: 120–180 direct clinical teaching/supervisory hours.",
  },
  img: {
    "Internal Medicine": "The cornerstone USCE clerkship — inpatient and outpatient IM with attendings experienced in residency-caliber LoRs.",
    "Family Medicine": "Community-based clerkships offering broad patient exposure and continuity narratives for ERAS.",
    "Pediatrics": "Ambulatory pediatric clerkships with documentation structured for residency applications.",
    "Psychiatry": "Psychiatry clerkships in outpatient and integrated behavioral health settings.",
  },
};

export const INTAKE_FIELDS = {};

export const SUITES = [
  {
    icon: "Landmark",
    title: "Practice Formation Architecture",
    desc: "Entity structuring, collaborative-to-autonomous transition mapping, and operational readiness for APRNs establishing independent practice under N.J.A.C. 13:35-6.17.",
  },
  {
    icon: "ScrollText",
    title: "Regulatory Compliance Suites",
    desc: "Documentation frameworks, joint protocol retirement pathways, and board-aligned recordkeeping engineered for full autonomous practice standing.",
  },
  {
    icon: "TrendingUp",
    title: "Operational Launch Systems",
    desc: "Credentialing, payer enrollment sequencing, and clinical workflow design so autonomous practices open with institutional-grade infrastructure from day one.",
  },
];

export const PREVIEW_GRID = [
  {
    id: "universities",
    icon: "GraduationCap",
    title: "Universities & Graduate Nursing",
    pains: ["Not enough clinical placements or preceptors for your students", "Placement paperwork and reporting overwhelming program staff"],
    deliverables: [
      "We handle the placement paperwork and reporting for you",
      "Live dashboards — attendance, clinical hours, and compliance at a glance",
      "Preceptors trained on your systems and grading rubrics (Typhon, Exxat)",
    ],
  },
  {
    id: "facilities",
    icon: "Hospital",
    title: "Healthcare Systems & FQHCs",
    pains: ["Provider burnout and growing chart backlogs", "Slow credentialing delaying new clinicians"],
    deliverables: [
      "Credentialing completed 60 days before start",
      "You keep 100% of billing revenue — no supervision cost to you",
      "Clinical teams trained on your EHR before day one",
    ],
  },
  {
    id: "pediatric",
    icon: "Baby",
    title: "High-Acuity Pediatric Facilities",
    pains: ["Not enough pediatric-trained staff for high-acuity units"],
    deliverables: [
      "Pediatric privacy and compliance standards built in",
      "Hire directly from our credentialed talent pool",
      "Dedicated pediatric NP student teams",
    ],
  },
  {
    id: "homecare",
    icon: "Home",
    title: "Homecare Agencies",
    pains: ["Not enough supervisory oversight as your patient census grows"],
    deliverables: [
      "Clear role boundaries between aides, LPNs, and nurse practitioners",
      "Staff trained on home health charting systems before arrival",
      "Supervised chart quality reviews and utilization checks",
    ],
  },
  {
    id: "lpn",
    icon: "BookOpen",
    title: "Vocational & Sub-Acute LPN Schools",
    pains: ["Losing clinical sites at nursing homes and sub-acute facilities", "NCLEX-PN pass rates at risk"],
    deliverables: [
      "Protection for NCLEX-PN first-time pass rates",
      "Teaching support and curriculum structuring",
      "State Board audit-ready compliance packages",
    ],
  },
  {
    id: "preceptors",
    icon: "Award",
    title: "Preceptors & Clinical Leaders",
    pains: ["Precepting burnout from unmanaged student loads"],
    deliverables: [
      "Simple 4-step onboarding with clear qualifying requirements",
      "We sponsor your national educator certifications (CNE, CNEcl)",
      "Support to launch your own independent practice (N.J.A.C. 13:35-6.17)",
    ],
  },
  {
    id: "img",
    icon: "Globe",
    title: "International Medical Schools (Global USCE)",
    pains: ["Unpredictable access to U.S. clinical experience"],
    deliverables: [
      "Reliable, properly supervised U.S. clinical rotations",
      "Verified recommendation letters delivered on ERAS timelines",
      "Team-based training alongside U.S. nursing cohorts",
    ],
  },
];

export const SPECIALTY_ENGINES = {
  universities: [
    {
      name: "PMHNP",
      cert: "PMHNP-BC",
      items: [
        "Active PMHNP / psychiatrist credentials with national certification (PMHNP-BC)",
        "1+ year specialty experience",
        "≥500 faculty-supervised clinical hours across two psychotherapy modalities",
        "NJ 24-month / 2,400-hour collaborative joint protocol adherence",
        "DEA / CDS registration compliance",
      ],
    },
    {
      name: "AGNP — Primary & Acute Care",
      cert: "AGPCNP-BC / AGACNP-BC",
      items: [
        "Population-specific certification (AGPCNP-BC or AGACNP-BC)",
        "1+ year specialty experience",
        "Restricted scope alignment by population focus",
        "Graduate pharmacology requirements",
        "NJ practice threshold standards",
      ],
    },
    {
      name: "PNP — Primary & Acute Care",
      cert: "CPNP-PC / CPNP-AC",
      items: [
        "CPNP-PC or CPNP-AC certification",
        "Pediatric clinical hour thresholds",
        "Child & family confidentiality compliance",
        "NJ APN private-practice rules",
      ],
    },
    {
      name: "FNP",
      cert: "FNP-C / FNP-BC",
      items: [
        "Lifespan primary care supervision",
        "FNP-C / FNP-BC certification with 1+ year experience",
        "3-credit graduate pharmacology (or 45 integrated hours)",
        "NJ prescriptive joint protocols",
      ],
    },
    {
      name: "MSN-Ed",
      cert: "Nurse Educator",
      items: [
        "Curriculum design, simulation & learner assessment",
        "LPN clinical instruction pathways",
        "NJ DOH-licensed facility RN preceptor standards (BSN/MSN/doctorate or 5 years experience)",
        "Business structuring scope within active RN licensure",
      ],
    },
    {
      name: "WHNP",
      cert: "WHNP-BC / CNM",
      items: [
        "WHNP-BC or CNM credentials",
        "Women's health clinical hour supervision",
        "Gynecologic assessment oversight",
        "NJ APN compliance standards",
      ],
    },
  ],
};

export const ADMIN_OFFLOAD = {
  title: "Administrative Offload & Real-Time Monitoring",
  desc: "Full administrative and reporting offload for client stakeholders — coupled with secure, real-time monitoring dashboards.",
  items: ["Live student attendance", "Clinical hour accruals", "Compliance clearances", "Audit-ready reporting exports"],
};

// Plain-language preceptor snapshot for public, ungated visitors (no login required).
export const PRECEPTOR_QUICK = {
  compRange: "$6,000–$8,500 per semester",
  compNote: "Exact amount depends on your specialty and how many students you take on. Paid as a 1099 independent-contractor honorarium.",
  perk: "2–3 hours/day of charting relief once a student is placed with you",
  steps: ["Apply (5 minutes)", "We verify your license & background", "You're matched with a student"],
  eligibility: [
    "Active, unrestricted nursing license",
    "Current national certification in your specialty",
    "1+ year of experience in that specialty",
  ],
};

// Public, no-dollar-figure summary of university subscription tiers. Full fee schedule is gated
// behind an Executive Transmittal request (see ExecutiveTransmittalModal).
export const SUBSCRIPTION_TIERS = [
  {
    name: "Tier 1",
    specialty: "PMHNP — Psychiatric-Mental Health",
    desc: "Our highest-demand specialty. These placements are scheduled first.",
  },
  {
    name: "Tier 2",
    specialty: "AGACNP — Adult-Gerontology Acute Care",
    desc: "Preceptors who specialize in hospital and acute-care settings.",
  },
  {
    name: "Tier 3",
    specialty: "Specialty Niche — WHNP / PNP",
    desc: "Women's health and pediatric specialty tracks.",
  },
  {
    name: "Tier 4",
    specialty: "Primary Care — FNP / AGPCNP",
    desc: "A broad network of primary care preceptors.",
  },
];
export const SUBSCRIPTION_TIER_NOTE =
  "Every tier comes with a guaranteed number of placements for your students — in writing, with clear remedies if we fall short. Full fee schedules, per-student pricing, and contract terms are shared privately in a secure Executive Transmittal after a short verification step — no pricing is published publicly.";

// Public, plain-language version of the 3-phase clinical pod model (mirrors the Health Systems track).
export const POD_PHASES = [
  {
    phase: "Phase 1",
    title: "Admin & EHR Scribing",
    desc: "Students start by shadowing and handling documentation support — learning your workflow and EHR system without adding to your workload.",
  },
  {
    phase: "Phase 2",
    title: "Collaborative Care",
    desc: "Students begin seeing patients directly under your supervision, with you countersigning and guiding clinical decisions in real time.",
  },
  {
    phase: "Phase 3",
    title: "Autonomous Practice Readiness",
    desc: "Students take on a fuller caseload with lighter-touch oversight, building the independent judgment they'll need after graduation.",
  },
];
